import type { Ref } from 'react';
import * as React from 'react';
import { forwardRef } from 'react';
import type { AutocompleteProps } from '@components/moleculs/autocomplete/types';
import type { AutocompleteOption } from '@models/types';
import MuiAutocomplete from '@mui/material/Autocomplete';
import type { PopperProps } from '@mui/material/Popper';
import { sameWidthModifier } from '@utils/popup-modifiers';
import cn from 'classnames';

import { ArrowIcon } from '../../icons';
import { Chip } from '../chip';
import { Popper } from '../popper';

import { AutocompleteFormControl } from './styles';

const AutocompleteComponent = forwardRef(
    <
        T,
        Multiple extends boolean | undefined,
        DisableClearable extends boolean | undefined,
        FreeSolo extends boolean | undefined,
    >(
        props: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
        ref: Ref<HTMLSelectElement>,
    ) => {
        const {
            fullWidth,
            className,
            popupIcon = <ArrowIcon />,
            renderTags = (value, getTagProps) =>
                value.map((opt, index) => (
                    <Chip
                        {...getTagProps({ index })}
                        label={(opt as unknown as AutocompleteOption<T>).label}
                    />
                )),
            ...rest
        } = props;

        return (
            <AutocompleteFormControl variant='standard' fullWidth={fullWidth} className={className}>
                <MuiAutocomplete<T, Multiple, DisableClearable, FreeSolo>
                    ref={ref}
                    PopperComponent={({ className: popperClassName, ...popperRest }) => (
                        <Popper
                            className={cn('autocomplete-popper', popperClassName)}
                            modifiers={[sameWidthModifier] as PopperProps['modifiers']}
                            {...popperRest}
                        />
                    )}
                    selectOnFocus={false}
                    {...rest}
                    renderTags={renderTags}
                    popupIcon={popupIcon}
                />
            </AutocompleteFormControl>
        );
    },
);

export const Autocomplete = AutocompleteComponent as <
    T,
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
    FreeSolo extends boolean | undefined,
>(
    props: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> & {
        ref?: Ref<HTMLSelectElement>;
    },
) => React.ReactElement;
